// Utilities to compute a UTC Date corresponding to a desired local time (HH:mm) in a given IANA timezone.
// Uses date-fns-tz for proper timezone handling, including DST transitions.

import { formatInTimeZone } from 'date-fns-tz';
import { addDays, isAfter } from 'date-fns';

/** Parse an HH:mm string into {hour, minute}. Throws on invalid. */
export function parseHHmm(hhmm: string): { hour: number; minute: number } {
  const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(hhmm);
  if (!m) throw new Error(`Invalid HH:mm format: ${hhmm}`);
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  return { hour, minute };
}

/**
 * Find the UTC Date for a given local date (YYYY-MM-DD) and target local time in the specified timezone.
 * Uses date-fns-tz to properly handle timezone conversions, including DST transitions.
 * 
 * This function correctly handles timezone conversion regardless of the system's local timezone.
 * It uses a search approach with formatInTimeZone to find the UTC time that, when displayed in
 * the target timezone, matches our desired local time. This avoids issues with fromZonedTime
 * which depends on the system timezone when extracting date components.
 * 
 * @param timeZone - IANA timezone string (e.g., 'America/Los_Angeles')
 * @param ymd - Date string in YYYY-MM-DD format
 * @param hhmm - Time string in HH:mm format
 * @returns UTC Date object representing the target local time in the specified timezone
 */
export function getUtcForLocalDateTime(
  timeZone: string,
  ymd: string,
  hhmm: string
): Date {
  const { hour: targetHour, minute: targetMinute } = parseHHmm(hhmm);

  // Parse date components
  const [year, month, day] = ymd.split('-').map(Number);

  // Search for the UTC time that, when formatted in the target timezone, gives us our target
  // We search a wide range (-24 to +48 hours) to handle timezones far from UTC (e.g., UTC+9, UTC+10)
  // This approach is system-timezone-independent and correctly handles DST transitions
  for (let utcHourOffset = -24; utcHourOffset <= 48; utcHourOffset++) {
    const candidate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
    candidate.setUTCHours(utcHourOffset, targetMinute, 0, 0);

    const formatted = formatInTimeZone(candidate, timeZone, 'yyyy-MM-dd HH:mm');
    const [candidateDate, candidateTime] = formatted.split(' ');
    const [candidateHour, candidateMin] = candidateTime.split(':').map(Number);

    if (candidateDate === ymd && candidateHour === targetHour && candidateMin === targetMinute) {
      return candidate;
    }
  }

  // Fallback (shouldn't happen in practice, but provides a reasonable default)
  return new Date(Date.UTC(year, month - 1, day, targetHour, targetMinute, 0));
}

/**
 * Compute the UTC instant representing today at HH:mm in the user's timezone; if already past now,
 * return the UTC instant for tomorrow at HH:mm.
 * 
 * Uses date-fns-tz for proper timezone-aware date arithmetic, including DST transitions.
 */
export function getUtcForLocalTimeTodayOrNext(
  timeZone: string,
  hhmm: string,
  now = new Date()
): Date {
  // Note: Logger import would create circular dependency, so we use console for this utility
  // In production, these debug logs won't appear unless NODE_ENV=development

  // Get today's date in the user's timezone using date-fns-tz
  const ymdToday = formatInTimeZone(now, timeZone, 'yyyy-MM-dd');

  // Calculate the target time today in the user's timezone
  const candidate = getUtcForLocalDateTime(timeZone, ymdToday, hhmm);

  // Check if the candidate time is in the future
  if (isAfter(candidate, now)) {
    return candidate;
  }

  // Time has passed, calculate for tomorrow
  // Use date-fns addDays for timezone-aware date arithmetic
  const tomorrowDate = addDays(now, 1);
  const ymdTomorrow = formatInTimeZone(tomorrowDate, timeZone, 'yyyy-MM-dd');

  const result = getUtcForLocalDateTime(timeZone, ymdTomorrow, hhmm);

  return result;
}

/**
 * Format a Date (UTC) as an ISO 8601 string with timezone offset for Brevo API.
 * 
 * This function takes a UTC Date and formats it as if it represents a local time
 * in the specified timezone, producing an ISO string with timezone offset.
 * 
 * Example: For 8am PST, this produces "2024-01-15T08:00:00.000-08:00"
 * 
 * @param utcDate - UTC Date object representing the target time
 * @param timeZone - IANA timezone string (e.g., 'America/Los_Angeles')
 * @returns ISO 8601 string with timezone offset (e.g., "2024-01-15T08:00:00.000-08:00")
 */
export function formatScheduledTimeForBrevo(
  utcDate: Date,
  timeZone: string
): string {
  // Note: Logger import would create circular dependency, so we use console for this utility
  // In production, these debug logs won't appear unless NODE_ENV=development

  // Format the UTC date as if it's in the user's timezone
  // This produces the ISO string with timezone offset that Brevo expects
  const result = formatInTimeZone(utcDate, timeZone, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");

  return result;
}
